import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface TourStep {
    target: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'bottom-end' | 'top-end' | 'left-start' | 'right-start';
}

interface TourTooltipProps {
    step: TourStep;
    currentStep: number;
    totalSteps: number;
    onNext: () => void;
    onPrevious: () => void;
    onSkip: () => void;
    onClose: () => void;
}

interface ArrowPosition {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
    transform?: string;
}

interface Position {
    top: number;
    left: number;
    arrowPosition: ArrowPosition;
}

// Constants
const SPACING = 16;
const ARROW_SIZE = 8;
const VIEWPORT_MARGIN = 20;
const RESIZE_DEBOUNCE = 200;
const POSITION_CALC_DELAY = 50;

// Specific positioning adjustments for each step
const POSITION_ADJUSTMENTS: Record<
    number,
    {
        tooltip?: { top?: number; left?: number };
        arrow?: { top?: number; left?: number; right?: number; bottom?: number };
    }
> = {
    0: {
        // User menu
        tooltip: { left: -185 },
        arrow: { left: 197 }
    },
    1: {
        // Toggle sidebar
        tooltip: { top: 56, left: -145 },
        arrow: { top: -8, left: 158 }
    },
    2: {
        // Dark mode
        tooltip: { top: 56, left: -290 },
        arrow: { top: -8, left: 310 }
    },
    3: {
        // Fullscreen
        tooltip: { top: 66, left: -293 },
        arrow: { top: -8, left: 310 }
    },
    4: {
        // Language
        tooltip: { top: 66, left: -293 },
        arrow: { top: -8, left: 310 }
    }
};

const TourTooltipComponent: React.FC<TourTooltipProps> = ({
    step,
    currentStep,
    totalSteps,
    onNext,
    onPrevious,
    onSkip
}) => {
    const { t } = useTranslation();
    const [position, setPosition] = useState<Position>({ top: 0, left: 0, arrowPosition: {} });
    const [isVisible, setIsVisible] = useState(false);
    const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const calculatingRef = useRef(false);
    const resizeTimeoutRef = useRef<number | null>(null);
    const visibilityTimeoutRef = useRef<number | null>(null);
    const positionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const mountedRef = useRef(true);

    // Get base position based on placement
    const getBasePosition = useCallback(
        (
            targetRect: DOMRect,
            tooltipRect: DOMRect,
            scrollY: number,
            scrollX: number,
            placement: string
        ): { top: number; left: number; arrowPosition: ArrowPosition } => {
            const targetCenterX = targetRect.left + targetRect.width / 2;
            const targetCenterY = targetRect.top + targetRect.height / 2;

            let top = 0;
            let left = 0;
            let arrowPosition: ArrowPosition = {};

            switch (placement) {
                case 'bottom':
                case 'bottom-end':
                    top = targetRect.bottom + scrollY + SPACING;
                    left = targetCenterX + scrollX - tooltipRect.width / 2;
                    arrowPosition = {
                        top: -ARROW_SIZE,
                        left: tooltipRect.width / 2,
                        transform: 'rotate(45deg)'
                    };
                    break;

                case 'top':
                case 'top-end':
                    top = targetRect.top + scrollY - tooltipRect.height - SPACING;
                    left = targetCenterX + scrollX - tooltipRect.width / 2;
                    arrowPosition = {
                        bottom: -ARROW_SIZE,
                        left: tooltipRect.width / 2,
                        transform: 'rotate(45deg)'
                    };
                    break;

                case 'left':
                case 'left-start':
                    top = targetCenterY + scrollY - tooltipRect.height / 2;
                    left = targetRect.left + scrollX - tooltipRect.width - SPACING;
                    arrowPosition = {
                        top: tooltipRect.height / 2,
                        right: -ARROW_SIZE,
                        transform: 'rotate(45deg)'
                    };
                    break;

                case 'right':
                case 'right-start':
                    top = targetCenterY + scrollY - tooltipRect.height / 2;
                    left = targetRect.right + scrollX + SPACING;
                    arrowPosition = {
                        top: tooltipRect.height / 2,
                        left: -ARROW_SIZE,
                        transform: 'rotate(45deg)'
                    };
                    break;

                default:
                    top = targetRect.bottom + scrollY + SPACING;
                    left = targetCenterX + scrollX - tooltipRect.width / 2;
                    arrowPosition = {
                        top: -ARROW_SIZE,
                        left: tooltipRect.width / 2,
                        transform: 'rotate(45deg)'
                    };
            }

            return { top, left, arrowPosition };
        },
        []
    );

    // Apply custom adjustments
    const applyAdjustments = useCallback(
        (
            pos: { top: number; left: number; arrowPosition: ArrowPosition },
            targetRect: DOMRect,
            scrollY: number,
            scrollX: number
        ): { top: number; left: number; arrowPosition: ArrowPosition } => {
            const adjustments = POSITION_ADJUSTMENTS[currentStep];
            if (!adjustments) return pos;

            const { top, left, arrowPosition } = pos;
            let adjustedTop = top;
            let adjustedLeft = left;

            if (adjustments.tooltip?.top !== undefined) {
                adjustedTop = targetRect.top + scrollY + adjustments.tooltip.top;
            }
            if (adjustments.tooltip?.left !== undefined) {
                adjustedLeft = targetRect.left + scrollX + adjustments.tooltip.left;
            }

            const adjustedArrowPosition: ArrowPosition = { ...arrowPosition };
            if (adjustments.arrow) {
                if (adjustments.arrow.top !== undefined) adjustedArrowPosition.top = adjustments.arrow.top;
                if (adjustments.arrow.left !== undefined) adjustedArrowPosition.left = adjustments.arrow.left;
                if (adjustments.arrow.right !== undefined) adjustedArrowPosition.right = adjustments.arrow.right;
                if (adjustments.arrow.bottom !== undefined) adjustedArrowPosition.bottom = adjustments.arrow.bottom;
            }

            return { top: adjustedTop, left: adjustedLeft, arrowPosition: adjustedArrowPosition };
        },
        [currentStep]
    );

    // Prevent viewport overflow
    const preventViewportOverflow = useCallback(
        (
            pos: { top: number; left: number; arrowPosition: ArrowPosition },
            tooltipRect: DOMRect
        ): { top: number; left: number; arrowPosition: ArrowPosition } => {
            const viewportWidth = window.innerWidth;
            const tooltipRight = pos.left + tooltipRect.width;

            if (tooltipRight > viewportWidth - VIEWPORT_MARGIN) {
                const overflow = tooltipRight - (viewportWidth - VIEWPORT_MARGIN);
                const adjustedLeft = pos.left - overflow;
                const adjustedArrowPosition: ArrowPosition = { ...pos.arrowPosition };

                if (adjustedArrowPosition.left !== undefined) {
                    adjustedArrowPosition.left = adjustedArrowPosition.left + overflow;
                }

                return { ...pos, left: adjustedLeft, arrowPosition: adjustedArrowPosition };
            }

            return pos;
        },
        []
    );

    // Main position calculation
    const calculatePosition = useCallback(() => {
        if (calculatingRef.current || !mountedRef.current) {
            return;
        }
        calculatingRef.current = true;
        const target = document.querySelector(step.target) as HTMLElement;

        if (!target || !tooltipRef.current) {
            calculatingRef.current = false;
            return;
        }

        // Verify target is visible
        const targetRect = target.getBoundingClientRect();
        if (targetRect.width === 0 || targetRect.height === 0) {
            calculatingRef.current = false;
            return;
        }

        setTargetElement(target);

        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const scrollY = window.scrollY;
        const scrollX = window.scrollX;

        // Calculate position in stages
        let pos = getBasePosition(targetRect, tooltipRect, scrollY, scrollX, step.placement || 'bottom');
        pos = applyAdjustments(pos, targetRect, scrollY, scrollX);
        pos = preventViewportOverflow(pos, tooltipRect);

        setPosition(pos);

        // Smooth visibility transition
        if (visibilityTimeoutRef.current) {
            clearTimeout(visibilityTimeoutRef.current);
        }

        visibilityTimeoutRef.current = window.setTimeout(() => {
            if (mountedRef.current) {
                setIsVisible(true);
            }
            calculatingRef.current = false;
        }, 0);
    }, [step.target, step.placement, getBasePosition, applyAdjustments, preventViewportOverflow]);

    // Calculate position when step changes
    useEffect(() => {
        setIsVisible(false);
        calculatingRef.current = false;

        positionTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current) {
                calculatePosition();
            }
        }, POSITION_CALC_DELAY);

        return () => {
            if (positionTimeoutRef.current) {
                clearTimeout(positionTimeoutRef.current);
                positionTimeoutRef.current = null;
            }
        };
    }, [step.target, calculatePosition]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
            }

            setIsVisible(false);
            calculatingRef.current = false;

            resizeTimeoutRef.current = window.setTimeout(() => {
                if (mountedRef.current) {
                    calculatePosition();
                }
            }, RESIZE_DEBOUNCE);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
                resizeTimeoutRef.current = null;
            }
        };
    }, [calculatePosition]);

    // Scroll target into view when step changes
    useEffect(() => {
        if (targetElement && mountedRef.current) {
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }
    }, [currentStep, targetElement]);

    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;

            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current);
                resizeTimeoutRef.current = null;
            }

            if (visibilityTimeoutRef.current) {
                clearTimeout(visibilityTimeoutRef.current);
                visibilityTimeoutRef.current = null;
            }

            if (positionTimeoutRef.current) {
                clearTimeout(positionTimeoutRef.current);
                positionTimeoutRef.current = null;
            }
        };
    }, []);

    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;

    return createPortal(
        <>
            {/* Overlay */}
            <div className="tour-overlay" onClick={onSkip} />

            {/* Spotlight */}
            {targetElement && (
                <div
                    className="tour-spotlight"
                    style={{
                        top: targetElement.getBoundingClientRect().top + window.scrollY - 4,
                        left: targetElement.getBoundingClientRect().left + window.scrollX - 4,
                        width: targetElement.offsetWidth + 8,
                        height: targetElement.offsetHeight + 8
                    }}
                />
            )}

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className={`tour-tooltip ${isVisible ? 'tour-tooltip--visible' : ''}`}
                style={{
                    top: `${position.top}px`,
                    left: `${position.left}px`
                }}
            >
                {/* Arrow */}
                {position.arrowPosition && (
                    <div
                        className="tour-tooltip__arrow"
                        style={{
                            top:
                                position.arrowPosition.top !== undefined
                                    ? `${position.arrowPosition.top}px`
                                    : undefined,
                            left:
                                position.arrowPosition.left !== undefined
                                    ? `${position.arrowPosition.left}px`
                                    : undefined,
                            right:
                                position.arrowPosition.right !== undefined
                                    ? `${position.arrowPosition.right}px`
                                    : undefined,
                            bottom:
                                position.arrowPosition.bottom !== undefined
                                    ? `${position.arrowPosition.bottom}px`
                                    : undefined,
                            transform: position.arrowPosition.transform
                        }}
                    />
                )}

                {/* Content */}
                <div className="tour-tooltip__content">
                    <p>{step.content}</p>
                </div>

                {/* Footer */}
                <div className="tour-tooltip__footer">
                    <div className="tour-tooltip__progress">
                        {currentStep + 1} / {totalSteps}
                    </div>
                    <div className="tour-tooltip__actions">
                        <button
                            className="tour-tooltip__button tour-tooltip__button--skip"
                            onClick={onSkip}
                            type="button"
                        >
                            {t('tourSkip')}
                        </button>
                        {!isFirstStep && (
                            <button
                                className="tour-tooltip__button tour-tooltip__button--default"
                                onClick={onPrevious}
                                type="button"
                            >
                                {t('tourPrevious')}
                            </button>
                        )}
                        <button
                            className="tour-tooltip__button tour-tooltip__button--default"
                            onClick={onNext}
                            type="button"
                        >
                            {isLastStep ? t('tourFinish') : t('tourNext')}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

// Memoize component to prevent unnecessary re-renders
export const TourTooltip = memo(TourTooltipComponent, (prevProps, nextProps) => {
    return (
        prevProps.currentStep === nextProps.currentStep &&
        prevProps.step.target === nextProps.step.target &&
        prevProps.step.content === nextProps.step.content &&
        prevProps.step.placement === nextProps.step.placement &&
        prevProps.totalSteps === nextProps.totalSteps
    );
});
